import React, { useState } from 'react';
import { Alert, Col, Row, Spin, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  filtersQueryKey,
  getSalesAnalyticsCustomers,
  type SalesAnalyticsFilterParams,
} from '@/services/salesAnalytics.service';
import { OiHorizontalBarChart } from '@/components/owner-intelligence/charts/OiCharts';
import { SalesAnalyticsToolbar } from './SalesAnalyticsToolbar';
import { SalesAnalyticsDrillDownDrawer } from './SalesAnalyticsDrillDownDrawer';

const { Title } = Typography;

export const SalesAnalyticsCustomersTab: React.FC<{
  filters: SalesAnalyticsFilterParams;
  filterSummary: string;
}> = ({ filters, filterSummary }) => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['sales-analytics', 'customers', filtersQueryKey(filters)],
    queryFn: () => getSalesAnalyticsCustomers(filters),
  });

  if (query.isLoading) return <Spin />;
  if (query.isError) return <Alert type="error" message={(query.error as Error).message} />;
  const d = query.data!;

  const printRows = d.top_customers.map((r) => ({
    customer: r.customer_name,
    revenue: formatCurrency(r.revenue),
    invoice_count: r.invoice_count,
  }));

  const selected = d.top_customers.find((c) => c.customer_id === selectedCustomerId);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          Customers &amp; segments
        </Title>
        <SalesAnalyticsToolbar
          tab="customers"
          tabTitle="Customers & Segments"
          filters={filters}
          filterSummary={filterSummary}
          printColumns={[
            { key: 'customer', label: 'Customer' },
            { key: 'revenue', label: 'Revenue', align: 'right' },
            { key: 'invoice_count', label: 'Invoices', align: 'right' },
          ]}
          printRows={printRows}
        />
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Title level={5}>Top customers by invoiced revenue</Title>
          <Table
            size="small"
            rowKey="customer_id"
            dataSource={d.top_customers}
            pagination={false}
            onRow={(r) => ({ onClick: () => setSelectedCustomerId(r.customer_id), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Customer', dataIndex: 'customer_name' },
              { title: 'Invoices', dataIndex: 'invoice_count', width: 90, align: 'right' },
              { title: 'Revenue', dataIndex: 'revenue', align: 'right', render: (v: number) => formatCurrency(v) },
              {
                title: '',
                key: '360',
                width: 100,
                render: (_: unknown, r) => (
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(r.link_path);
                    }}
                  >
                    Customer 360
                  </a>
                ),
              },
            ]}
          />
        </Col>
        <Col xs={24} lg={10}>
          <Title level={5}>Revenue by country</Title>
          <OiHorizontalBarChart
            data={d.revenue_by_country.map((r) => ({ name: r.country, value: r.revenue }))}
            dataKey="value"
            nameKey="name"
            height={240}
          />
        </Col>
      </Row>

      <Title level={5} style={{ marginTop: 24 }}>
        Open pipeline by industry
      </Title>
      <OiHorizontalBarChart
        data={d.pipeline_by_industry.map((r) => ({ name: r.industry, value: r.value }))}
        dataKey="value"
        nameKey="name"
        height={200}
      />

      <SalesAnalyticsDrillDownDrawer
        open={Boolean(selected)}
        onClose={() => setSelectedCustomerId(null)}
        title={selected ? selected.customer_name : ''}
        subtitle={`${selected?.invoice_count ?? 0} invoices · ${filterSummary}`}
        rows={
          selected
            ? [
                {
                  key: selected.customer_id,
                  label: selected.customer_name,
                  sublabel: 'Total invoiced revenue in period',
                  amount: selected.revenue,
                  link_path: selected.link_path,
                },
              ]
            : []
        }
        formatCurrency={formatCurrency}
      />
    </div>
  );
};
